import { useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getLandingData } from '../redux/actions/landing-actions';
import BannerHoverx4 from 'legacyfrontend/components/home/bannerComponents/BannerHoverx4';
import BannerSimpleX3 from 'legacyfrontend/components/home/bannerComponents/BannerSimpleX3';
import Carousel3 from 'legacyfrontend/components/home/bannerComponents/Carousel3';
import CarouselBanners from 'legacyfrontend/components/home/bannerComponents/CarouselBanners';
import FeaturedSeventyFiveTwentyFive from 'legacyfrontend/components/home/bannerComponents/FeaturedSeventyFiveTwentyFive';
import Featuredx2 from 'legacyfrontend/components/home/bannerComponents/Featuredx2';
import FullWidthBanner from 'legacyfrontend/components/home/bannerComponents/FullWidthBanner';
import OffersGalery from 'legacyfrontend/components/home/bannerComponents/OffersGalery';
import VideoBanner from 'legacyfrontend/components/home/bannerComponents/video';
import { Seo } from 'core/components/seo';
import { useAppName } from 'legacyfrontend/hooks/useSafeState';
import { mapLandingBannerToHomeBanner } from './landingBannerMapper';
import { getLandingSlugSegment } from '../utils/slugify';

function selectLandingData(state) {
  return state.landing?.data ?? null;
}

function getComponentByUbication(component, ubicacionObj, componentIndex, ubicacionIndex) {
  const componentsMap = {
    'destacado-2-lugares-75-25': FeaturedSeventyFiveTwentyFive,
    '2-banner-ancho-total': Featuredx2,
    'galeria-ofertas': OffersGalery,
    'banner-zocalo-x3': BannerSimpleX3,
    'carrusel-4-elementos': CarouselBanners,
    'carrusel-3-elementos': Carousel3,
    video: VideoBanner,
    'banner-simple-ancho-total': FullWidthBanner,
    'banner-hover-x4': BannerHoverx4,
  };

  const categoryName = component.categoria.nombre;
  const ubicationName = ubicacionObj?.nombre;
  const TheComponent = componentsMap[ubicationName];

  return (
    TheComponent !== undefined && (
      <TheComponent
        key={`${ubicationName}-${categoryName}-${component.banner[0].id}-${ubicacionObj.id}-${componentIndex}${ubicacionIndex !== undefined ? `-${ubicacionIndex}` : ''}`}
        data={component}
      />
    )
  );
}

function mapComponent(component, index) {
  if (component.categoria?.ubicacion) {
    return Array.isArray(component.categoria.ubicacion)
      ? component.categoria.ubicacion.map((ubicacion, ubicacionIndex) =>
          getComponentByUbication(component, ubicacion, index, ubicacionIndex)
        )
      : getComponentByUbication(component, component.categoria.ubicacion, index);
  }
  return null;
}

function mapLandingCategoryToHomeCategory(cat) {
  const ubicaciones = (cat.categoriaUbicaciones || [])
    .map((item) => item?.ubicacion)
    .filter(Boolean);

  return {
    categoria: {
      ...cat,
      ubicacion: ubicaciones.length <= 1 ? ubicaciones[0] : ubicaciones,
      descripcion: cat.descripcion || cat.nombre,
    },
    banner: (cat.empresaBanners || []).map(mapLandingBannerToHomeBanner),
  };
}

export default function LandingView() {
  const { id, slug } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const data = useSelector(selectLandingData);
  const appName = useAppName();

  const dataMatchesId = data && id != null && Number(data.id) === Number(id);

  useEffect(() => {
    if (!id) return;
    if (dataMatchesId) return;
    let cancelled = false;

    dispatch(getLandingData(id))
      .then((result) => {
        if (!cancelled && result == null) {
          history.replace('/');
        }
      })
      .catch(() => {
        if (!cancelled) {
          history.replace('/');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, dataMatchesId, dispatch, history]);

  useEffect(() => {
    if (!dataMatchesId || !data) return;

    const slugSegment = getLandingSlugSegment(data);
    if (!slugSegment) return;

    if (!slug || slug !== slugSegment) {
      history.replace(`/landing/${id}/${encodeURIComponent(slugSegment)}`);
    }
  }, [data, dataMatchesId, id, slug, history]);

  const titulo = data?.titulo || '';
  const descripcion = data?.descripcion || '';
  const categoriasBanner = data?.categoriasBanner || [];
  const categoriasActivas = categoriasBanner.filter((cat) => cat.activo && cat.empresaBanners?.length);

  const categoriasParaRender = useMemo(() => {
    return categoriasActivas
      .map(mapLandingCategoryToHomeCategory)
      .filter((item) => {
        if (!item.categoria?.ubicacion) return false;
        if (!item.banner?.length) return false;
        return true;
      });
  }, [categoriasActivas]);

  const renderComponents = useMemo(() => {
    let theComponents = [];

    categoriasParaRender.forEach((component, index) => {
      theComponents = theComponents.concat(mapComponent(component, index));
    });

    return theComponents.filter(Boolean);
  }, [categoriasParaRender]);

  if (!dataMatchesId) {
    return (
      <>
        <Seo
          title={appName}
          description={descripcion || `Descubrí nuestras ofertas y promociones en ${appName}.`}
        />
        <div className="max-w-4xl mx-auto px-4 py-12 flex justify-center items-center min-h-[40vh]">
          <p className="text-gray-500">Cargando...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo
        title={appName}
        description={descripcion || `Descubrí nuestras ofertas y promociones en ${appName}.`}
      />
      <main className="max-w-[1200px] mx-auto px-4 py-6">
      <header className="mb-8">
        {titulo && (
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{titulo}</h1>
        )}
        {descripcion && (
          <div className="text-base leading-relaxed text-gray-600 whitespace-pre-wrap">
            {descripcion}
          </div>
        )}
      </header>

      <div className="space-y-10">
        {renderComponents}
      </div>
    </main>
      </>
    );
  }
